import axios, { AxiosRequestConfig, CancelTokenSource, ResponseType } from 'axios';
import { useCallback } from 'react';

export type RequestHeader = { [key: string]: string };
export type Query = { [key: string]: unknown };

/**
 * APIレスポンスのインターフェース
 */
export interface ApiResponse {
  status: number;
  errorCode?: string;
  isError: boolean;
  data: unknown;
  headers: unknown;
}

/**
 * APIエラーのインターフェース
 */
export class ApiError extends Error {
  constructor(public response: ApiResponse) {
    super();
  }
}

/**
 * APIエラー情報
 */
export type OnApiError = (resp: ApiResponse) => ApiResponse | void;

/**
 * APIリクエスト情報
 */
export class InternalApi {
  private path: string;

  private canceler: CancelTokenSource;

  private fatalError?: OnApiError = undefined;

  private error?: OnApiError = undefined;

  private ignoreTimeout: boolean = false;

  private config: AxiosRequestConfig = {
    headers: {
      'Content-Type': 'application/json',
    },
    responseType: 'json',
    withCredentials: true,
    timeout: 30000,
  };

  constructor(path: string) {
    this.path = path;
    this.canceler = axios.CancelToken.source();
    this.config.cancelToken = this.canceler.token;
  }

  auth(username: string, password: string) {
    // this.config.auth = { username, password };
    this.config.headers = {
      'Content-Type': 'application/json',
      Authorization: `Basic ${btoa(`${encodeURIComponent(username)}:${encodeURIComponent(password)}`)}`,
    };
    return this;
  }

  useResponseTypeBlob() {
    this.config.responseType = 'blob';
    return this;
  }

  header(headers: RequestHeader) {
    this.config.headers = { ...this.config.headers, 'X-Accept-Token': this.getToken(), ...headers };
    return this;
  }

  getToken() {
    const token = sessionStorage.getItem('token');
    const newToken = window.session?.token;
    if (newToken && newToken !== '' && token !== newToken) {
      sessionStorage.setItem('token', newToken);
      return newToken as string;
    }
    return token;
  }

  noHeader() {
    this.config.headers = { 'X-Accept-Token': this.getToken() };
    return this;
  }

  skipTimeout() {
    this.ignoreTimeout = true;
    return this;
  }

  private async request(method: 'get' | 'post' | 'put' | 'delete'): Promise<ApiResponse> {
    this.config.method = method;
    this.config.url = this.buildPath();

    // try {
    const resp = await axios.request(this.config).catch((error) => {
      // console.warn(error);
      const code = error.response?.status;
      const response: ApiResponse = {
        status: code,
        isError: true,
        errorCode: error.response?.data?.code,
        data: error.response?.data,
        headers: error.response?.headers,
      };

      if (this.fatalError && (code === 401 || code === 403 || code === 500)) {
        const res1 = this.fatalError(response);
        if (res1) {
          return res1;
        }
      } else if (this.error) {
        const res = this.error(response);
        if (res) {
          return res;
        }
      }

      return response;
    });

    if (resp.status === undefined && this.ignoreTimeout === false) {
      const response: ApiResponse = {
        status: -1,
        isError: true,
        errorCode: 'TIMEOUT',
        data: undefined,
        headers: undefined,
      };
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw new ApiError(response);
    }

    return {
      status: resp.status,
      errorCode: resp.data?.code,
      isError: resp.status >= 400,
      data: resp.data,
      headers: resp.headers,
    };
    // } catch (error) {
    //   throw error;
    // }
  }

  private data(data?: unknown) {
    this.config.data = data;
    return this;
  }

  cancel() {
    this.canceler?.cancel();
  }

  onError(onError: OnApiError) {
    this.error = onError;
    return this;
  }

  onFatalError(onError: OnApiError) {
    this.fatalError = onError;
    return this;
  }

  get(query?: Query) {
    this.config.params = query;
    return this.request('get');
  }

  post(data?: unknown) {
    return this.data(data).request('post');
  }

  put(data?: unknown) {
    return this.data(data).request('put');
  }

  delete(query?: Query) {
    this.config.params = query;
    return this.request('delete');
  }

  private buildPath() {
    return this.path;
  }
}

/**
 * APIリクエストコンポーネント
 */
export type FetchApi = {
  auth: (path: string, username: string, password: string) => InternalApi;
  path: (path: string) => InternalApi;
};

/**
 * API実行用コンポーネント
 */
export function useApi(): FetchApi {
  const onFatalError = useCallback(() => {}, []);

  return {
    auth: (path: string, username: string, password: string) => {
      return new InternalApi(path).auth(username, password);
    },
    path: (path: string) => new InternalApi(path).onFatalError(onFatalError),
  };
}
